import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  VStack,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Badge,
  Spinner,
  useColorModeValue
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import axios from "axios";
import Loading from "../resources/Loading";
import { Link } from "react-router-dom";

export default function Inbox() {
  const [chats, setChats] = useState([]);
  const [filteredChats, setFilteredChats] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const authToken = localStorage.getItem("authToken");
  const authemail = localStorage.getItem("authemail");

  const bgColor = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const response = await axios.get("https://api.agromanch.com/api/newchats", {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setChats(response.data);
        setFilteredChats(response.data);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    // Initial fetch
    fetchChats();

    // Fetch chats every second
    const intervalId = setInterval(fetchChats, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [authToken]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredChats(chats);
    } else {
      const filtered = chats.filter(chat => 
        chat.user.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChats(filtered);
    }
  }, [searchQuery, chats]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const truncateMessage = (message, maxLength = 30) => {
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner color="green.500" />
      </Flex>
    );
  }

  return (
    <Box>
      <Box p={3} borderBottomWidth="1px" borderColor={borderColor}>
        <Heading size="md" mb={3}>Messages</Heading>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search conversations..."
            borderRadius="full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Box>

      <VStack spacing={0} align="stretch" maxH="60vh" overflowY="auto">
        {filteredChats.length === 0 ? (
          <Flex justify="center" align="center" h="200px" direction="column">
            <Text color="gray.500" fontSize="sm">No conversations yet</Text>
            <Text color="gray.400" fontSize="xs" mt={1}>
              Start a new conversation from product pages
            </Text>
          </Flex>
        ) : (
          filteredChats.map((chat, index) => {
            const isUserSender = chat.to === authemail;
            const chatPartner = isUserSender ? chat.from : chat.to;
            
            return (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <Link to={`/chat/${chat.product_id}/${chatPartner}`}>
                  <Box 
                    p={3} 
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                    cursor="pointer"
                  >
                    <Flex justify="space-between">
                      <HStack spacing={3}>
                        <Avatar 
                          src={chat.user.picture} 
                          name={chat.user.name} 
                          size="sm"
                        />
                        <Box>
                          <Text fontWeight="medium" fontSize="sm">
                            {chat.user.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {truncateMessage(chat.message)}
                          </Text>
                        </Box>
                      </HStack>
                      
                      <VStack spacing={1} align="flex-end">
                        <Text fontSize="xs" color="gray.500">
                          {formatTime(chat.createdAt)}
                        </Text>
                        {chat.unread > 0 && (
                          <Badge 
                            colorScheme="green" 
                            borderRadius="full" 
                            fontSize="xs"
                            px={2}
                          >
                            {chat.unread}
                          </Badge>
                        )}
                      </VStack>
                    </Flex>
                  </Box>
                </Link>
              </React.Fragment>
            );
          })
        )}
      </VStack>
    </Box>
  );
}
